import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { fetchEbooks, isEbookUnlocked, ebookUnlockExpiresIn, Ebook } from '../api/ebookApi';
import './EbookReaderScreen.css';

type Lang = 'bn' | 'it' | 'en';

const CONTENT = {
  bn: {
    loading: 'লোড হচ্ছে...',
    notFound: 'বই পাওয়া যায়নি',
    notUnlocked: 'এই বইটি আনলক করা নেই',
    goBack: 'বইয়ের তালিকায় ফিরুন',
    expiresIn: 'মেয়াদ শেষ',
    days: 'দিন',
    hours: 'ঘণ্টা',
    noPdf: 'পিডিএফ এখনো উপলব্ধ নয়',
    openExternal: 'বাহ্যিক ব্রাউজারে খুলুন',
  },
  it: {
    loading: 'Caricamento...',
    notFound: 'Libro non trovato',
    notUnlocked: 'Questo libro non è sbloccato',
    goBack: 'Torna alla Libreria',
    expiresIn: 'Scade tra',
    days: 'giorni',
    hours: 'ore',
    noPdf: 'PDF non ancora disponibile',
    openExternal: 'Apri nel Browser',
  },
  en: {
    loading: 'Loading...',
    notFound: 'Book not found',
    notUnlocked: 'This book is not unlocked',
    goBack: 'Back to Library',
    expiresIn: 'Expires in',
    days: 'days',
    hours: 'hrs',
    noPdf: 'PDF not available yet',
    openExternal: 'Open in Browser',
  },
};

const EbookReaderScreen: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { currentLanguage } = useLanguage();
  const lang = currentLanguage as Lang;
  const t = CONTENT[lang] || CONTENT.en;

  const [ebook, setEbook] = useState<Ebook | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetchEbooks().then(books => {
      const found = books.find(b => String(b.id) === id);
      setEbook(found || null);
      setLoading(false);
    });
  }, [id]);

  const getLang = (ml: { it: string; bn: string; en: string }) => ml[lang] || ml.en || ml.it || '';

  const getExpiryLabel = (ebookId: number): string => {
    const ms = ebookUnlockExpiresIn(ebookId);
    if (ms <= 0) return '';
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    if (days >= 1) return `${t.expiresIn} ${days} ${t.days}`;
    const hours = Math.floor(ms / (1000 * 60 * 60));
    return `${t.expiresIn} ${hours} ${t.hours}`;
  };

  if (loading) {
    return (
      <div className="ebook-reader-screen loading">
        <div className="ebook-reader-spinner" />
        <p>{t.loading}</p>
      </div>
    );
  }

  if (!ebook) {
    return (
      <div className="ebook-reader-screen error">
        <p>📚 {t.notFound}</p>
        <button onClick={() => navigate('/ebooks')}>{t.goBack}</button>
      </div>
    );
  }

  if (!isEbookUnlocked(ebook.id)) {
    return (
      <div className="ebook-reader-screen error">
        <p>🔒 {t.notUnlocked}</p>
        <button onClick={() => navigate('/ebooks')}>{t.goBack}</button>
      </div>
    );
  }

  const expiry = getExpiryLabel(ebook.id);

  return (
    <div className="ebook-reader-screen">
      {/* Top bar */}
      <div className="ebook-reader-topbar">
        <button className="ebook-reader-back" onClick={() => navigate('/ebooks')}>←</button>
        <div className="ebook-reader-title-wrap">
          <h2>{getLang(ebook.title_ml)}</h2>
          {expiry && <span className="ebook-reader-expiry">⏳ {expiry}</span>}
        </div>
        {ebook.pdf_url && (
          <button
            className="ebook-reader-external-btn"
            onClick={() => window.open(ebook.pdf_url, '_blank')}
            title={t.openExternal}
          >
            ↗
          </button>
        )}
      </div>

      {/* PDF viewer */}
      <div className="ebook-reader-body">
        {!ebook.pdf_url ? (
          <div className="ebook-reader-no-pdf">
            <span>📄</span>
            <p>{t.noPdf}</p>
          </div>
        ) : (
          <>
            {pdfLoading && (
              <div className="ebook-reader-pdf-loading">
                <div className="ebook-reader-spinner" />
              </div>
            )}
            {/* Use Google Docs viewer as universal PDF embedder that works on all platforms */}
            <iframe
              className="ebook-reader-iframe"
              src={`https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(ebook.pdf_url)}`}
              title={getLang(ebook.title_ml)}
              onLoad={() => setPdfLoading(false)}
              allow="fullscreen"
            />
          </>
        )}
      </div>
    </div>
  );
};

export default EbookReaderScreen;
